import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SectionHeader from "@src/components/widgets/SectionHeader/SectionHeader";
import {
  GridContainer,
  PaginationControls,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import PxProductTile from "@src/components/widgets/PxProductTile";
import DistributorLogo from "@src/components/common/DistributorLogo/DistributorLogo";
import { distributorSlugToName } from "@src/constants/Distributors";
import * as ProductApi from "@src/api/ProductApi";
import { Product } from "@src/types/product";
import { PaginationValues } from "@src/types/pagination";
import styles from "./PromotedProductsPage.module.scss";

const DEFAULT_PAGINATION: PaginationValues = {
  pageSize: 20,
  page: 1,
  totalPages: 0,
  totalCount: 0,
};

interface RouteParams extends Record<string, string | undefined> {
  distributorSlug: string;
}

function PromotedProductsPage(): React.JSX.Element {
  const { showToast } = useToast();
  const { distributorSlug } = useParams<RouteParams>();
  const [products, setProducts] = useState<Product[]>([]);
  const [pagination, setPagination] =
    useState<PaginationValues>(DEFAULT_PAGINATION);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!distributorSlug) return;
    loadProducts({ page: 1, pageSize: DEFAULT_PAGINATION.pageSize });
  }, [distributorSlug]);

  const loadProducts = async (override: Partial<PaginationValues>) => {
    if (!distributorSlug) return;
    setLoading(true);
    try {
      const next = { ...pagination, ...override };
      const response = await ProductApi.getPromotedDistributorProducts(
        distributorSlug,
        { page: next.page, pageSize: next.pageSize },
      );
      const body = response.body ?? {};
      const results: Product[] = body.results ?? [];
      setProducts(results);
      setPagination({
        pageSize: next.pageSize,
        page: body.page ?? next.page,
        totalPages: body.totalPages ?? body.total_pages ?? 1,
        totalCount:
          body.totalCount ?? body.total_count ?? body.count ?? results.length,
      });
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error loading promoted products",
        variant: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationFilterChanged = ({
    name,
    value,
  }: {
    name: string;
    value: number;
  }) => {
    const updated = { ...pagination, [name]: value };
    if (name === "pageSize") {
      updated.page = 1;
    }
    loadProducts(updated);
  };

  const distributorName = distributorSlug
    ? distributorSlugToName(distributorSlug)
    : "";

  return (
    <div>
      {loading && <SpinningLoader />}
      <SectionHeader
        header={<span className={styles.pageTitle}>Promoted Products</span>}
        rightSideContent={
          !!distributorSlug && (
            <DistributorLogo
              distributorSlug={distributorSlug}
              height={30}
              ariaLabel={`${distributorName} Promoted Products`}
            />
          )
        }
      />
      <div className="padding-top-xxl">
        {products.length === 0 && !loading ? (
          <div className={styles.emptyState}>
            <p>No promoted products available for {distributorName}.</p>
          </div>
        ) : (
          <>
            <GridContainer columns={{ base: 5 }} gap="var(--margin-sm)">
              {products.map((product, index) => (
                <div
                  key={`promoted-products-page-${product.id ?? index}`}
                >
                  <PxProductTile
                    product={product}
                    className="width-100-important"
                  />
                </div>
              ))}
            </GridContainer>
            <div className="padding-top-xxl">
              <PaginationControls
                onChange={handlePaginationFilterChanged}
                values={pagination}
                data-testid="promoted-products-page-pagination-controls"
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default PromotedProductsPage;